import type { HistoryRange } from "../api/types.js";
import type { CashRow, Change, ChartSlot, ComingModel, HeroModel, HoldingRow, HoldingSheetModel, InvestModel, OrdersModel } from "../app/invest-model.js";
import { LAPSED_COPY } from "../app/kyc-model.js";
import { LOCKED_HOME } from "../app/security-model.js";
import { el } from "../lib/dom.js";
import { keepTogether } from "../lib/keep.js";
import { icon } from "../lib/icon.js";
import type { AimHandlers } from "./aim.js";
import { chartError, chartSkeleton, lineChart, rangeControl, type LineChart } from "./chart.js";
import { PILL, titledBanner } from "./home.js";
import { frame } from "./sheet.js";
import { tabBar, type Tab } from "./tabs.js";

/**
 * Invest (DCTA-229; docs/design/v3/ux-specs/10-invest.md, mock-up 20-invest.html, shared.css §15): the customer's investments
 * home — the bank's total and how it moved, one chart read by finger or keys, the holdings, the orders, what is coming, and
 * one way to buy: the assistant's Ask tile (its mark, its colour), which opens the chat as a tap on the mark does. A
 * read-only bank screen: nothing here signs, trades or changes the assistant, and no sheet of it carries the mark.
 *
 * States (§5): loading (the default's layout as quiet bars, one status line), default, a day being read, any range, the
 * bank's fallbacks (no chart; no range control), the chart failed (Retry in the plot), the bank unreachable (the banner over
 * what was there), no shares, no trading account, money locked, the identity check lapsed. Every string is text.
 */
export interface InvestHandlers {
  onTab(tab: Tab): void;
  readonly assistant: AimHandlers;
  /** The bank unreachable: read everything again. */
  onRetry(): void;
  /** The chart failed: read the history again. */
  onRetryChart(): void;
  /** A range pressed (not the one shown). */
  onRange(range: HistoryRange): void;
  onOpenHolding(instrumentId: string): void;
  /** An order's or a held buy's receipt ("bundle:step", or a ledger ref) — a held one with its Cancel. */
  onOpenReceipt(key: string): void;
  onOpenMandate(mandateId: string): void;
  onSeeAllOrders(): void;
  onUnlock(): void;
  onUpdateKyc(): void;
}

type Holdings = Extract<InvestModel, { readonly kind: "holdings" }>;
/** The plot's slot as the view draws it: the model's, or the quiet block while the bank is read. */
export type PlotSlot = ChartSlot | { readonly kind: "loading" };

/** The hero's nodes the reading writes to, and the total it returns to. */
interface HeroNodes {
  readonly root: HTMLElement;
  readonly label: HTMLElement;
  readonly figure: HTMLElement;
  readonly total: string;
}
type RangeControl = ReturnType<typeof rangeControl>;
/** The plot's slot on the page: its node, and the line when there is one. */
interface PlotNodes {
  readonly node: HTMLElement;
  readonly line: LineChart | undefined;
}

/** The tab's own plot height (--chart-h) and the sheet's (--chart-h-sheet): the drawings' coordinate systems. */
const CHART_H = 176;
const SHEET_CHART_H = 148;

/** The shape the skeleton draws before the first read: the default's own parts, so the heights match (§5 loading). */
const SKELETON: Holdings = {
  kind: "holdings",
  banners: { locked: false, lapsed: false },
  hero: { total: "S$0,000.00", demo: true, change: { pct: "+0.00%", tone: "up", amount: "+S$00.00", words: "past month" } },
  chart: { kind: "none" },
  range: "1m",
  note: "Prices as of 00 Sep, 00:00 · US$1 = S$0.0000",
  holdings: [
    { id: "sk1", ticker: "AAAA", name: "Holding name", units: "0 shares", price: "S$00.00", value: "S$0,000.00", since: { text: "+00.00%", loss: false } },
    { id: "sk2", ticker: "AAA", name: "Holding name", units: "0 shares", price: "S$00.00", value: "S$000.00", since: { text: "+00.00%", loss: false } },
  ],
  cash: { name: "Trading settlement ···0000", amount: "S$0,000.00", held: [] },
  orders: { rows: [{ key: "sk", ticker: "AAA", name: "Holding name", units: "0 shares", price: "S$00.00", when: "Bought today, 00:00", amount: "S$000.00", state: "Filled" }] },
  coming: { rows: [] },
};

const noTranslate = <T extends HTMLElement>(node: T): T => {
  node.setAttribute("translate", "no");
  return node;
};
const tickerTile = (ticker: string | undefined): HTMLElement =>
  ticker === undefined ? el("span", { class: "ic tk", aria: { hidden: "true" } }, [icon("i-trend")]) : noTranslate(el("span", { class: "ic tk" }, [ticker]));
/** A sub-line's second fact: inline and unbroken in normal type, its own line without its dot in large type (§3). */
const second = (fact: string, dot: boolean): HTMLElement => el("span", { class: "nx" }, [...(dot ? [el("span", { class: "dot" }, ["· "])] : []), fact]);
/** The change, as the hero and the holding sheet say it: the pill (its tone), then the amount and its words. */
const changeLine = (c: Pick<Change, "pct" | "tone" | "amount">, words: string): HTMLElement[] => [
  el("span", { class: PILL[c.tone] }, [c.pct]),
  el("span", {}, [el("span", { class: "money" }, [c.amount]), ` ${words}`]),
];
/** A row that opens something (a button), or — in the skeleton — the same row as a plain block. */
const row = (cls: string, parts: readonly HTMLElement[], open: (() => void) | undefined, data: Readonly<Record<string, string>> = {}): HTMLElement => {
  if (open === undefined) return el("div", { class: cls }, parts);
  const b = el("button", { class: `raw ${cls}`, type: "button", data }, parts);
  b.addEventListener("click", open);
  return b;
};

function empty(title: string, line: string): HTMLElement {
  return el("div", { class: "empty" }, [el("span", { class: "ic", aria: { hidden: "true" } }, [icon("i-pie")]), el("b", {}, [title]), el("p", {}, [line])]);
}

export class InvestView {
  readonly parts: readonly HTMLElement[];
  readonly body: HTMLElement;
  readonly #h: InvestHandlers;
  /** The bank unreachable (09's line): over whatever was there, never instead of it. */
  readonly #error: HTMLElement;
  readonly #status: HTMLElement;
  #content: HTMLElement;
  #hero: HeroNodes | undefined;
  #chart: PlotNodes | undefined;
  #range: RangeControl | undefined;

  constructor(handlers: InvestHandlers) {
    this.#h = handlers;
    const topbar = el("div", { class: "topbar" }, [
      el("span", { class: "topbar-pad", aria: { hidden: "true" } }),
      el("span", { class: "t" }, ["Invest"]),
      el("span", { class: "topbar-pad", aria: { hidden: "true" } }),
    ]);
    const retry = el("button", { class: "raw", type: "button" }, ["Retry"]);
    retry.addEventListener("click", () => handlers.onRetry());
    this.#error = el("div", { class: "banner", role: "status", hidden: true }, [
      el("span", { class: "bic", aria: { hidden: "true" } }, [icon("i-alert")]),
      el("span", {}, ["Can't reach the bank right now. Nothing has changed."]),
      retry,
    ]);
    this.#status = el("span", { class: "vis-hidden", role: "status" });
    this.#content = el("div", { class: "ivc" });
    this.body = el("div", { class: "body" }, [this.#error, this.#status, this.#content]);
    const bar = tabBar("invest", { onTab: (t) => handlers.onTab(t), ...handlers.assistant });
    this.parts = [topbar, this.body, bar.dock];
  }

  /** The first read in flight: the default's layout as quiet bars, inert and hidden from a screen reader, which hears one line. */
  loading(): void {
    this.#error.hidden = true;
    this.#status.textContent = "Loading your investments…";
    const content = this.#holdings(SKELETON, { kind: "loading" }, false);
    content.classList.add("skel-mask");
    content.setAttribute("aria-hidden", "true");
    content.inert = true;
    this.#swap(content, undefined, undefined, undefined);
  }

  show(m: InvestModel): void {
    this.#error.hidden = true;
    this.#status.textContent = "";
    if (m.kind === "no-account") this.#swap(el("div", { class: "ivc" }, [empty("No trading account", "Buying shares needs a trading account.")]), undefined, undefined, undefined);
    else if (m.kind === "empty") this.#swap(this.#empty(m), undefined, undefined, undefined);
    else this.#holdings(m, m.chart.kind === "none" ? undefined : m.chart, true);
  }

  /**
   * A range's answer (§6.2): the change line and the chart only — the rows, the scroll and the focus stay. The new line
   * replaces the old with the design's fade (it arrives held at 40 %, then the frame lets go). A different structure
   * (the bank no longer has the read) draws the whole tab.
   */
  showChart(m: InvestModel): void {
    const old = this.#chart;
    const hero = this.#hero;
    if (m.kind !== "holdings" || old === undefined || hero === undefined || m.chart.kind === "none") {
      this.show(m);
      return;
    }
    const fresh = this.#heroNode(m.hero);
    hero.root.replaceWith(fresh.root);
    this.#hero = fresh;
    const next = this.#plotFor(m.chart);
    next.line?.busy(true);
    old.line?.dispose();
    // Focus in the old plot (its line, its Retry) moves to what replaced it, never to the page.
    const focused = old.node.contains(document.activeElement);
    old.node.replaceWith(next.node);
    this.#chart = next;
    this.#range?.set(m.range);
    if (focused) (next.node.querySelector<HTMLElement>(".plot[role='slider'], .cerr button") ?? this.#range?.node.querySelector<HTMLElement>("[aria-pressed='true']"))?.focus({ preventScroll: true });
    const line = next.line;
    if (line !== undefined) requestAnimationFrame(() => line.busy(false));
  }

  /** The range was pressed: the frame stays while the bank answers, the old line held at 40 % (no skeleton flash). */
  chartBusy(): void {
    this.#chart?.line?.busy(true);
  }

  /** The read failed: what is on screen stays; the banner says so and offers Retry. */
  unreachable(): void {
    this.#status.textContent = "";
    this.#error.hidden = false;
  }

  /** A row by what it opens (rebuilt by every draw): where focus goes back to when a layer closes (WCAG 2.4.3). */
  row(data: "open" | "mandate" | "key", value: string): HTMLElement | undefined {
    return [...this.body.querySelectorAll<HTMLElement>(`button.item[data-${data}]`)].find((b) => b.dataset[data] === value);
  }

  /** The new content, and the parts a range's answer replaces (only a drawn tab has them; the old chart lets go of the page). */
  #swap(content: HTMLElement, hero: HeroNodes | undefined, chart: PlotNodes | undefined, range: RangeControl | undefined): void {
    this.#chart?.line?.dispose();
    this.#content.replaceWith(content);
    this.#content = content;
    this.#hero = hero;
    this.#chart = chart;
    this.#range = range;
  }

  #banners(b: { readonly locked: boolean; readonly lapsed: boolean }): HTMLElement[] {
    return [
      // DCTA-231 (the coordinator, 15 Oct): Home's lock banner words — the bank's lock sentence (LOCKED_HOME); Me › Security's
      // status row keeps its short "Payments are stopped" (Umakhan's copy for a status row).
      ...(b.locked
        ? [titledBanner({ cls: "banner lock", icon: "i-lock", title: LOCKED_HOME.title, line: [LOCKED_HOME.line], action: "Unlock", actionCls: "raw", onAction: () => this.#h.onUnlock() })]
        : []),
      ...(b.lapsed
        ? [titledBanner({ cls: "banner dueban kyc", icon: "i-id", title: LAPSED_COPY.title, line: ["Buying waits until it's updated."], action: LAPSED_COPY.update, actionCls: "raw upd", onAction: () => this.#h.onUpdateKyc() })]
        : []),
    ];
  }

  #heroNode(h: HeroModel): HeroNodes {
    const label = el("span", { class: "t" }, ["Investments"]);
    const figure = el("div", { class: "big money" }, [h.total]);
    const root = el("div", { class: "ivh", data: { reading: "false" } }, [
      el("div", { class: "lbl" }, [label, ...(h.demo ? [el("span", { class: "flag" }, ["Demo prices"])] : [])]),
      figure,
      el("div", { class: "chg" }, h.change === undefined ? [] : changeLine(h.change, h.change.words)),
    ]);
    return { root, label, figure, total: h.total };
  }

  /**
   * The plot's slot: the bank's line, a quiet block while it is read, or the failure and Retry — all the same height; under
   * 4 points the hero stands alone (no line from three dots), the slot kept as an empty placeholder for the next answer.
   */
  #plotFor(slot: PlotSlot): PlotNodes {
    if (slot.kind === "few") return { node: el("div", { class: "plot-few", hidden: true, aria: { hidden: "true" } }), line: undefined };
    if (slot.kind === "failed") return { node: chartError(() => this.#h.onRetryChart()), line: undefined };
    if (slot.kind !== "chart") return { node: chartSkeleton(), line: undefined };
    const line = lineChart(slot.chart, { height: CHART_H, onRead: (i) => this.#read(i === null ? undefined : slot.chart.points[i]) });
    return { node: line.element, line };
  }

  /** A day is read: the label line is that day, the figure that day's value; the change row goes quiet (CSS: visibility). */
  #read(p: { readonly day: string; readonly money: string } | undefined): void {
    const h = this.#hero;
    if (h === undefined) return;
    h.root.dataset["reading"] = p === undefined ? "false" : "true";
    h.label.textContent = p === undefined ? "Investments" : p.day;
    h.figure.textContent = p === undefined ? h.total : p.money;
  }

  /** The tab with holdings — or (`live` false) the skeleton of it. `plot`: absent = no chart and no range control (§4.3). */
  #holdings(m: Holdings, plot: PlotSlot | undefined, live: boolean): HTMLElement {
    const hero = this.#heroNode(m.hero);
    const blocks: HTMLElement[] = [...this.#banners(m.banners), hero.root];
    let chart: PlotNodes | undefined;
    let range: RangeControl | undefined;
    if (plot !== undefined) {
      chart = this.#plotFor(plot);
      blocks.push(chart.node);
      range = rangeControl(m.range, (r) => this.#h.onRange(r));
      blocks.push(range.node);
    }
    if (m.note !== undefined) blocks.push(el("p", { class: "ivnote" }, [m.note]));
    blocks.push(
      el("div", { class: "sec" }, [el("h3", { id: "iv-hold-h" }, ["Holdings"]), el("span", { class: "cap", aria: { hidden: "true" } }, ["Since you bought"])]),
      el("div", { class: "list feed", role: "group", aria: { labelledby: "iv-hold-h" } }, [...m.holdings.map((r) => this.#holdingRow(r, live)), ...(m.cash === undefined ? [] : [cashRowNode(m.cash)])]),
      this.#ask(live),
      ...this.#orders(m.orders, live),
      ...this.#coming(live ? m.coming : { rows: [] }, live),
      el("div", { class: "home-end", aria: { hidden: "true" } }),
    );
    const content = el("div", { class: "ivc" }, blocks);
    if (live) this.#swap(content, hero, chart, range);
    return content;
  }

  #empty(m: Extract<InvestModel, { readonly kind: "empty" }>): HTMLElement {
    return el("div", { class: "ivc" }, [
      ...this.#banners(m.banners),
      empty("No shares yet", "Shares you buy show here."),
      ...(m.cash === undefined ? [] : [el("div", { class: "list feed" }, [cashRowNode(m.cash)])]),
      this.#ask(true),
      ...this.#orders(m.orders, true),
      ...this.#coming(m.coming, true),
      el("div", { class: "home-end", aria: { hidden: "true" } }),
    ]);
  }

  #holdingRow(r: HoldingRow, live: boolean): HTMLElement {
    return row(
      "item",
      [
        tickerTile(r.ticker),
        el("span", { class: "tx" }, [noTranslate(el("b", {}, [r.name])), el("small", {}, [r.units, " ", second(r.price, true)])]),
        el("span", { class: "amt money" }, [
          r.value,
          ...(r.since === undefined ? [] : [el("small", { class: r.since.loss ? "neg" : "pos" }, [r.since.text, el("span", { class: "vis-hidden" }, [" since you bought"])])]),
        ]),
      ],
      live ? () => this.#h.onOpenHolding(r.id) : undefined,
      { open: r.id },
    );
  }

  /** The one way to buy (§6.4): Home's Ask tile — the assistant's surface — opening the chat as the mark does, text first. */
  #ask(live: boolean): HTMLElement {
    const mark = live ? [el("span", { class: "ai-mark", aria: { hidden: "true" } })] : [];
    return row("tile ask card ivask", [el("span", { class: "ic" }, mark), el("span", {}, [el("b", {}, ["Invest or add cash"]), el("small", {}, ["Ask the assistant"])])], live ? () => this.#h.assistant.onAssistant("text") : undefined);
  }

  #orders(o: OrdersModel | undefined, live: boolean): HTMLElement[] {
    if (o === undefined) return [];
    const all = o.rows.length === 0 ? [] : [live ? el("button", { class: "raw all", type: "button" }, ["See all", icon("i-chev-r")]) : el("span", { class: "all" }, ["See all"])];
    all[0]?.addEventListener("click", () => this.#h.onSeeAllOrders());
    const rows = o.rows.map((r) =>
      row(
        "item",
        [
          tickerTile(r.ticker),
          el("span", { class: "tx" }, [
            noTranslate(el("b", {}, [r.name])),
            ...(r.units === undefined ? [] : [el("small", {}, [r.units, ...(r.price === undefined ? [] : [" ", second(`at ${r.price}`, false)])])]),
            el("small", {}, keepTogether(r.when)),
          ]),
          el("span", { class: "amt money" }, [r.amount, el("small", {}, [r.state])]),
        ],
        live ? () => this.#h.onOpenReceipt(r.key) : undefined,
        { key: r.key },
      ),
    );
    return [
      el("div", { class: "sec" }, [el("h3", { id: "iv-ord-h" }, ["Orders"]), ...all]),
      el("div", { class: "list feed", role: "group", aria: { labelledby: "iv-ord-h" } }, rows.length > 0 ? rows : [el("span", { class: "quiet" }, ["No orders yet."])]),
    ];
  }

  #coming(c: ComingModel | undefined, live: boolean): HTMLElement[] {
    if (c === undefined) return [];
    const rows = c.rows.map((r) =>
      row(
        "item",
        [
          el("span", { class: "ic", aria: { hidden: "true" } }, [icon(r.ic)]),
          el(
            "span",
            { class: "tx" },
            r.kind === "buy"
              ? [noTranslate(el("b", {}, [r.title])), el("small", {}, ["When the market opens"]), el("small", {}, keepTogether(r.when))]
              : [el("b", {}, [r.title]), el("small", {}, [r.rule, " ", second(r.when, true)])],
          ),
          // A capped buy says "up to" before its figure, as the card does; the design draws the words under it.
          el(
            "span",
            { class: "amt money" },
            r.kind === "buy" && r.upTo ? [el("span", { class: "vis-hidden" }, ["up to "]), r.amount, el("small", { aria: { hidden: "true" } }, ["up to"])] : [r.amount],
          ),
        ],
        live ? () => (r.kind === "buy" ? this.#h.onOpenReceipt(r.key) : this.#h.onOpenMandate(r.mandateId)) : undefined,
        r.kind === "topup" ? { mandate: r.mandateId } : { key: r.key },
      ),
    );
    return [
      el("div", { class: "sec" }, [el("h3", { id: "iv-cu-h" }, ["Coming up"])]),
      el("div", { class: "list feed", role: "group", aria: { labelledby: "iv-cu-h" } }, rows.length > 0 ? rows : [el("span", { class: "quiet" }, ["Nothing coming up."])]),
    ];
  }
}

/** Cash to invest (13-account's words): the trading account's available balance; what a held buy keeps aside, by the bank's amount. */
function cashRowNode(c: CashRow): HTMLElement {
  return el("div", { class: "item" }, [
    el("span", { class: "ic tk", aria: { hidden: "true" } }, ["S$"]),
    el("span", { class: "tx" }, [el("b", {}, ["Cash to invest"]), el("small", {}, keepTogether(c.name)), ...c.held.map((h) => el("small", {}, [h]))]),
    el("span", { class: "amt money" }, [c.amount]),
  ]);
}

export interface HoldingSheetHandlers {
  /** Closes the sheet and opens the chat as a tap on the mark does — no words carried. */
  onBuyMore(): void;
  onDone(): void;
  /** A range pressed, or (the plot's Retry) the shown one asked again. */
  onRange(range: HistoryRange): void;
}

/**
 * §6.3 the holding sheet: the success sheet's header (ticker tile, name, market and units, the value, the return), the
 * holding's own chart and range control, its key-value rows (stacked in large type), then "Buy more with the assistant"
 * (a ghost button) and Done. The bank's surface: no assistant mark on it, no trade button. Its chart arrives by `setChart`.
 */
export function holdingSheetView(s: HoldingSheetModel, h: HoldingSheetHandlers): { readonly sheet: HTMLElement; setChart(slot: PlotSlot, range: HistoryRange): void } {
  const since = s.since;
  const buy = el("button", { class: "raw btn ghost", type: "button" }, ["Buy more with the assistant"]);
  buy.addEventListener("click", () => h.onBuyMore());
  const done = el("button", { class: "raw btn link done", type: "button" }, ["Done"]);
  done.addEventListener("click", () => h.onDone());
  let plot: HTMLElement = chartSkeleton();
  let line: LineChart | undefined;
  let shown: HistoryRange = "1m";
  const range = rangeControl(shown, (r) => {
    shown = r;
    // The frame stays while the bank answers: the old line held at 40 %.
    line?.busy(true);
    h.onRange(r);
  });
  const note = s.note === undefined ? [] : [el("p", { class: "ivnote" }, [s.note])];
  const slot = el("div", { class: "hchart" }, [plot, range.node, ...note]);
  const sheet = frame(s.name, noTranslate(el("div", { class: "tkm", aria: { hidden: "true" } }, [s.ticker])), [
    el("div", { class: "sum" }, [s.sum]),
    el("div", { class: "amount money" }, [s.value]),
    el("div", { class: "ivchg" }, since === undefined ? [] : changeLine(since, "since you bought")),
    slot,
    el(
      "div",
      { class: "kv stack" },
      s.rows.map((r) => el("div", {}, [el("span", { class: "k" }, [r.k]), el("span", { class: "v money" }, [r.v, ...(r.small === undefined ? [] : [el("small", {}, [r.small])])])])),
    ),
    el("div", { class: "acts" }, [buy, done]),
  ]);
  const title = sheet.querySelector("h2");
  if (title !== null) noTranslate(title);
  const setChart = (next: PlotSlot, r: HistoryRange): void => {
    shown = r;
    range.set(r);
    line?.dispose();
    line = undefined;
    // §4.3 rung 2: no instrument history — the sheet keeps everything but its chart (and the range that only serves it).
    range.node.hidden = next.kind === "none";
    let node: HTMLElement | undefined;
    if (next.kind === "chart") {
      line = lineChart(next.chart, { height: SHEET_CHART_H });
      node = line.element;
    } else if (next.kind === "failed") node = chartError(() => h.onRange(shown));
    else if (next.kind === "loading") node = chartSkeleton();
    if (node === undefined) plot.remove();
    else if (plot.isConnected) plot.replaceWith(node);
    else slot.prepend(node);
    plot = node ?? plot;
  };
  return { sheet, setChart };
}
